<template>
    <div>
        <router-link
            to="/home"
            class="btn btn-outline-secondary btn-sm float-end d-print-none"
        >
            {{ $t('lesson.viewAllSeries') }}
        </router-link>

        <h3
            v-if="series.title"
            class="mb-3 d-print-none"
        >
            {{ seriesTitle }}
        </h3>

        <div
            v-if="isLoading"
            class="alert alert-warning d-print-none"
            role="alert"
        >
            {{ $t('loading') }}...
        </div>

        <div class="row">
            <div class="col-md-4 col-12 d-print-none">
                <div class="list-group mb-5">
                    <a
                        v-for="lesson in rows"
                        :key="lesson.id"
                        href="#"
                        class="list-group-item list-group-item-action"
                        :class="{
                            active: (lesson.id === selectedLesson.id),
                        }"
                        @click.stop.prevent="selectLesson(lesson)"
                    >
                        {{ getTranslatedTitle(lesson) }}
                    </a>
                </div>
            </div>

            <div class="col">
                <div v-show="rows.length">
                    <div
                      v-if="Object.keys(languageOptions).length"
                      class="card d-print-none"
                    >
                        <div class="card-body">
                            <div class="form-group">
                                <label for="language_id">
                                    {{ $t('lesson.lessonLanguage') }}
                                </label>

                                <form-select
                                    v-model="languageId"
                                    name="language_id"
                                    :options="languageOptions"
                                />
                            </div>
                        </div>
                    </div>

                    <div class="card d-print-none">
                        <h5 class="card-header">
                            {{ $t('lesson.shareOptions') }}
                        </h5>

                        <div class="card-body">
                            <div class="row">
                                <div class="col">
                                    <button
                                        class="btn btn-primary w-100"
                                        @click.prevent="goToSharedPage(selectedLesson)"
                                    >
                                        {{ $t('lesson.viewPrinterFriendlyVersion') }}
                                    </button>
                                </div>

                                <div class="col">
                                    <button
                                        class="btn btn-primary w-100"
                                        @click="viewShareLink(selectedLesson)"
                                    >
                                        {{ $t('lesson.shareLesson') }}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <components-list
                        v-if="selectedLesson.id"
                        :lesson-id="selectedLesson.id"
                        :language-id="languageId"
                        :series-id="seriesId"
                    />

                    <media-list
                        v-if="selectedLesson.id"
                        :lesson-id="selectedLesson.id"
                        :language-id="languageId"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, toRaw } from "vue";

const Vue = require('vue');

import axios from 'axios';
import FormSelect from '../../form-components/FormSelect.vue';
import ComponentsList from './ComponentsList';
import MediaList from './MediaList';
import Swal from 'sweetalert2';
import useClipboard from 'vue-clipboard3'
import { find } from "lodash";

export default {
    components: {
        FormSelect,
        ComponentsList,
        MediaList,
    },

    props: {
        seriesId: {
            type: Number,
            required: true,
        },
    },

    data() {
        return {
            isLoading: false,
            series: {},
            rows: [],
            selectedLesson: {},
            languageId: null,
            languageOptions: {},
        };
    },

    setup() {
        const { toClipboard } = useClipboard();
        const shareUrl = ref("");

        const copyShareUrl = async () => {
            try {
                await toClipboard(shareUrl.value)
            } catch (e) {
                console.error(e)
            }
        }

        return {
            copyShareUrl,
            shareUrl,
        }
    },

    computed: {
        isSharable: function() {
            return (this.selectedLesson && this.selectedLesson.is_sharable);
        },

        seriesTitle: function() {
          if(this.series.translations_parsed && this.series.translations_parsed[this.languageId]) {
            return this.series.translations_parsed[this.languageId];
          } else {
            return this.series.title;
          }
        },
    },

    mounted: function () {
        this.getSeries();
        this.getLessons();
        this.getLanguageOptions();
    },

    watch: {
        languageId: function (newValue) {
            // change page title
            document.title = `Mustard Seeds - Series - ${this.seriesTitle}`;

            axios({
                method: 'get',
                url: `/subscriber/api/languages/${newValue}/`,
            })
            .then((response) => {
                this.$i18n.locale = response.data.code.toLowerCase();
            });
        },
    },

    methods: {
        getSeries: function () {
            this.isLoading = true;

            axios({
                method: 'get',
                url: `/subscriber/api/series/${this.seriesId}/`,
            })
            .then((response) => {
                this.series = response.data;
                document.title = `Mustard Seeds - ${this.$t('series.series')} - ${this.seriesTitle}`;
            });
        },

        getLessons: function () {
            this.isLoading = true;

            axios({
                method: 'get',
                url: `/subscriber/api/series/${this.seriesId}/lessons/`,
            })
            .then((response) => {
                this.rows = response.data;
                this.selectedLesson = this.rows[0];
                this.isLoading = false;
            });
        },

        selectLesson: function (lesson) {
            this.selectedLesson = lesson;
        },

        getShareLink: function (lesson) {
            return axios({
                method: 'get',
                url: `/subscriber/api/lessons/${lesson.id}/share/`,
                params: {
                    language_id: this.languageId,
                },
            })
        },

        goToSharedPage: async function (lesson) {
            if (!this.isSharable) {
                Swal.fire({
                    type: 'warning',
                    text: this.$t('lesson.shareRestrictionWarning'),
                    showCloseButton: true,
                });
            } else {
                // get shareable link from API
                const response = await this.getShareLink(lesson);

                window.location = response.data;
            }
        },

        getLanguageOptions() {
          axios({
                method: 'get',
                url: `/subscriber/api/series/${this.seriesId}/languages-options`,
            })
            .then((response) => {
                this.languageOptions = response.data;

                if(this.$app.languageId && this.languageOptions[this.$app.languageId]) {
                  this.languageId = Number(this.$app.languageId);
                } else {
                  this.languageId = Number(this.$app.translationEnglishId);
                }

                this.languageId = Number(this.$app.languageId || this.$app.translationEnglishId);
            });
        },

        viewShareLink: async function (lesson) {
            if(! this.isSharable) {
                Swal.fire({
                    type: 'warning',
                    text: this.$t('lesson.shareRestrictionWarning'),
                    showCloseButton: true,
                });
            } else {
                // get shareable link from API
                const response = await this.getShareLink(lesson);

                this.shareUrl = response.data;

                Swal.fire({
                    html: `
                            <h5 class="mb-3">
                                ${this.$t('lesson.shareInstructions', { duration: this.$app.signedUrlDurationDays })}
                            </h5>
                            <p>
                                ${this.$t('lesson.seriesIntroductionNotice')}
                            </p>
                            <p>
                                <code>${this.shareUrl}</code>
                            </p>
                        `,
                    footer: this.$t('lesson.shareLinkWarning'),
                    showCloseButton: true,
                    showConfirmButton: true,
                    confirmButtonText: this.$t('lesson.copyLink'),
                    width: '43rem',
                })
                .then((result) => {
                    this.copyShareUrl();
                })
            }

        },

        printLesson() {
            this.$window.print();
        },

        getTranslatedTitle(lesson) {
            const locale = this.$i18n.locale;

            const translation = find(toRaw(lesson.title_components), (row) => {
              return (row.language.code.toLowerCase() === locale.toLowerCase());
            });

            return (translation ? translation['content'] : this.$t(lesson.title));
        },
    },
};
</script>
